/**
 * DashboardOrg — Organization-wide KPI dashboard for executives.
 *
 * Shows org-level aggregates, department breakdown, and drill-down to members/KPIs.
 * Requires org.kpi.view_all permission.
 */
import React, { useEffect, useState } from 'react';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Users from 'lucide-react/dist/esm/icons/users';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import { Spinner } from '../../../components/ui';
import { useKPIStore } from '../../../stores/kpiStore';
import KPIAssignmentCard from './KPIAssignmentCard';
import type { DepartmentKPIs, TeamMemberKPIs } from '../../../services/api/kpi';

interface DashboardOrgProps {
  onSelectAssignment: (id: string) => void;
}

const DashboardOrg: React.FC<DashboardOrgProps> = ({ onSelectAssignment }) => {
  const {
    orgDashboard, orgDashboardLoading, fetchOrgDashboard, activeCycleId,
  } = useKPIStore();

  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchOrgDashboard();
  }, [activeCycleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDept = (deptId: string) => {
    setExpandedDepts(prev => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return next;
    });
  };

  const toggleMember = (userId: string) => {
    setExpandedMembers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  if (orgDashboardLoading && !orgDashboard) {
    return (
      <div className="flex items-center justify-center h-48 text-content-muted">
        <Spinner size="md" />
      </div>
    );
  }

  if (!orgDashboard || !orgDashboard.cycle) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-surface-primary border border-subtle flex items-center justify-center mb-4">
          <Building2 size={24} className="text-content-muted" />
        </div>
        <h3 className="text-base font-semibold text-content mb-1">No Active Cycle</h3>
        <p className="text-xs text-content-muted max-w-sm">
          There is no active performance cycle. Create one to see organization-wide KPI data.
        </p>
      </div>
    );
  }

  const { org_summary, overall_score, departments, members } = orgDashboard;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Org Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryCard label="Total KPIs" value={org_summary.total} />
        <SummaryCard label="On Track" value={org_summary.on_track} color="text-status-success" />
        <SummaryCard label="At Risk" value={org_summary.at_risk} color="text-status-warning" />
        <SummaryCard label="Behind" value={org_summary.behind} color="text-status-error" />
        <SummaryCard label="Not Started" value={org_summary.not_started} />
        <SummaryCard
          label="Overall Score"
          value={overall_score !== null ? `${Math.round(overall_score)}%` : '—'}
          color="text-content"
        />
      </div>

      {/* Department Breakdown */}
      {departments.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-[11px] text-content-tertiary uppercase tracking-wider">By Department</h3>
          {departments.map((dept) => {
            const key = dept.department_id || 'unassigned';
            const isExpanded = expandedDepts.has(key);
            const deptMembers = members.filter(m =>
              m.assignments.some(a => {
                if (!dept.department_id) return true;
                return true;
              })
            );

            return (
              <div key={key} className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
                <button
                  onClick={() => toggleDept(key)}
                  className="w-full flex items-center gap-3 p-4 text-left border-none outline-none cursor-pointer hover:bg-surface-secondary/30 transition-colors"
                >
                  {isExpanded
                    ? <ChevronDown size={14} className="text-content-muted shrink-0" />
                    : <ChevronRight size={14} className="text-content-muted shrink-0" />
                  }
                  <Building2 size={14} className="text-content-tertiary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-content">{dept.department_name}</span>
                    <span className="text-[11px] text-content-muted ml-2">
                      {dept.member_count} {dept.member_count === 1 ? 'member' : 'members'} · {dept.kpi_count} KPIs
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] tabular-nums shrink-0">
                    {dept.summary.on_track > 0 && <span className="text-status-success">{dept.summary.on_track} on track</span>}
                    {dept.summary.at_risk > 0 && <span className="text-status-warning">{dept.summary.at_risk} at risk</span>}
                    {dept.summary.behind > 0 && <span className="text-status-error">{dept.summary.behind} behind</span>}
                    {dept.avg_score !== null && (
                      <span className="font-semibold text-content">{Math.round(dept.avg_score)}%</span>
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-subtle px-4 pb-4 pt-2 space-y-2">
                    {deptMembers.length > 0 ? deptMembers.map(member => (
                      <MemberCard
                        key={member.user_id}
                        member={member}
                        isExpanded={expandedMembers.has(member.user_id)}
                        onToggle={() => toggleMember(member.user_id)}
                        onSelectAssignment={onSelectAssignment}
                      />
                    )) : (
                      <p className="text-xs text-content-muted py-2">No members with KPIs in this department.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* All Members */}
      <div className="space-y-2">
        <h3 className="text-[11px] text-content-tertiary uppercase tracking-wider">
          All Members ({members.length})
        </h3>
        {members.map(member => (
          <MemberCard
            key={member.user_id}
            member={member}
            isExpanded={expandedMembers.has(member.user_id)}
            onToggle={() => toggleMember(member.user_id)}
            onSelectAssignment={onSelectAssignment}
          />
        ))}
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: string | number; color?: string }> = ({
  label, value, color = 'text-content-secondary',
}) => (
  <div className="p-3 rounded-xl bg-surface-primary border border-subtle">
    <p className="text-[10px] text-content-muted uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-lg font-bold tabular-nums ${color}`}>{value}</p>
  </div>
);

const MemberCard: React.FC<{
  member: TeamMemberKPIs;
  isExpanded: boolean;
  onToggle: () => void;
  onSelectAssignment: (id: string) => void;
}> = ({ member, isExpanded, onToggle, onSelectAssignment }) => (
  <div className="rounded-xl border border-subtle overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 p-3 text-left border-none outline-none cursor-pointer hover:bg-surface-secondary/30 transition-colors"
    >
      {isExpanded
        ? <ChevronDown size={12} className="text-content-muted shrink-0" />
        : <ChevronRight size={12} className="text-content-muted shrink-0" />
      }
      <div className="w-7 h-7 rounded-full bg-surface-secondary flex items-center justify-center text-[10px] font-bold text-content-secondary shrink-0">
        {(member.display_name || member.email)[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-content">{member.display_name || member.email}</span>
        {member.title && <span className="text-[11px] text-content-muted ml-2">{member.title}</span>}
      </div>
      <div className="flex items-center gap-2 text-[11px] tabular-nums shrink-0">
        <span className="text-content-muted">{member.assignments.length} KPIs</span>
        {member.overall_score !== null && (
          <span className="font-semibold text-content">{Math.round(member.overall_score)}%</span>
        )}
      </div>
    </button>

    {isExpanded && member.assignments.length > 0 && (
      <div className="border-t border-subtle p-3 grid gap-2">
        {member.assignments.map(a => (
          <KPIAssignmentCard
            key={a.id}
            assignment={a}
            onClick={() => onSelectAssignment(a.id)}
          />
        ))}
      </div>
    )}
  </div>
);

export default React.memo(DashboardOrg);
